import { Component, inject, signal, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { LanguageService } from '../../services/language.service';
import { CookieService } from '../../services/cookie.service';
import { UiStateService } from '../../services/ui-state.service';
import { RegionService } from '../../services/region.service';
import { ToastService } from '../../services/toast.service';
import { ClientInfo } from '../../shared/types/redirect.model';
import { LsButtonComponent } from '../ls-button/ls-button.component';
import { LoginMoreInfoComponent } from '../login-more-info/login-more-info.component';

@Component({
  selector: 'app-register-invite-code',
  standalone: true,
  imports: [TranslocoModule, ReactiveFormsModule, LsButtonComponent, LoginMoreInfoComponent],
  templateUrl: './register-invite-code.component.html',
  styleUrls: ['./register-invite-code.component.scss']
})
export class RegisterInviteCodeComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly cookieService = inject(CookieService);
  private readonly uiStateService = inject(UiStateService);
  private readonly regionService = inject(RegionService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  readonly submitting = signal(false);
  readonly showMoreInfo = signal(false);
  readonly inviteCodeControl = new FormControl('', [Validators.required]);

  clients: ClientInfo[] = [];
  inviteCode = '';

  ngOnInit(): void {
    this.uiStateService.hideLanguageSelector();
  }

  onBack(): void {
    this.uiStateService.showLanguageSelectorFn();
    this.router.navigate(['/']);
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.inviteCodeControl.markAsTouched();

    if (this.inviteCodeControl.invalid || this.submitting()) {
      return;
    }

    this.submitting.set(true);

    const inviteCode = this.inviteCodeControl.value!.trim();

    this.regionService.lookupByInvitationCode(inviteCode).subscribe({
      next: (results) => {
        if (!results || results.length === 0) {
          // No results - show error
          this.toastService.error('register.invalidCode', 10000);
          this.submitting.set(false);
        } else if (results.length === 1) {
          // Found - redirect to the portal URL
          const result = results[0];
          const redirectUrl = this.buildRedirectUrl(result.portalUrl, inviteCode);
          window.location.href = redirectUrl;
        } else {
          // Multiple results - show login-more-info for client selection
          this.inviteCode = inviteCode;
          this.clients = results.map(r => ({
            clientId: r.clientID,
            region: r.region,
            clientName: r.clientName,
            portalUrl: r.portalUrl
          }));
          this.showMoreInfo.set(true);
        }
      },
      error: () => {
        this.toastService.error('register.invalidCode', 10000);
        this.submitting.set(false);
      }
    });
  }

  private buildRedirectUrl(portalUrl: string, inviteCode: string): string {
    const lang = this.languageService.getCurrentLanguage();
    const cookieConsent = this.cookieService.getCookie('ls_allow_analytics');
    
    let url = `${portalUrl}/register/${encodeURIComponent(inviteCode)}?lang=${lang}`;
    if (cookieConsent) {
      url += `&cookie_consent=${cookieConsent}`;
    }
    return url;
  }
}
